import type { ChartConfiguration } from "chart.js";

// One stacked-bar dataset per rank position ("1st".."5th") plus an optional
// "Other". The Nth dataset holds the Nth-ranked driver's ride count in each bucket.
export type DriverRankSeries = {
  label: string; // "1st" … "5th" | "Other"
  data: number[]; // one count per bucket, aligned with `buckets`
};

export type DriverBucketRow = {
  driver_id: string;
  driver_name: string;
  ride_count: number;
  percent: number;
};

export type DriverBucketTable = {
  label: string; // the bucket label, e.g. "2025"
  total: number;
  rows: DriverBucketRow[]; // full ranking for the bucket (for the drill-down table)
};

// Stacked bars: each bar is one time bucket showing its top 5 drivers + "Other".
export type DriverBarData = {
  kind: "bar";
  buckets: string[]; // e.g. ["2024", "2025"]
  series: DriverRankSeries[];
  point_names: string[][]; // [bucketIndex][rank] -> real driver name (ranks 0..4)
  bucket_tables: DriverBucketTable[]; // aligned with `buckets`
};

// A single all-time pie, the long tail folded into an "Other" slice server-side.
export type DriverPieData = {
  kind: "pie";
  labels: string[];
  values: number[];
};

export type DriverActivityData = DriverBarData | DriverPieData;

// Distinct colors for ranks 1–5; "Other" gets a muted tone so it recedes.
const RANK_COLORS = [
  "#7B9669", // jade-500
  "#404E3B", // forest-900
  "#6C8480", // sage-500
  "#C7A27C", // clay
  "#8E7CC3", // iris
];
const OTHER_COLOR = "#BAC8B1"; // moss-200

export function buildChartConfig(data: DriverActivityData): ChartConfiguration {
  return data.kind === "pie" ? buildPieConfig(data) : buildBarConfig(data);
}

function buildPieConfig(data: DriverPieData): ChartConfiguration {
  return {
    type: "pie",
    data: {
      labels: data.labels,
      datasets: [
        {
          data: data.values,
          backgroundColor: data.labels.map((_, index) => RANK_COLORS[index % RANK_COLORS.length]),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
    },
  };
}

function buildBarConfig(data: DriverBarData): ChartConfiguration {
  const datasets = data.series.map((series, index) => ({
    type: "bar" as const,
    label: series.label,
    data: series.data,
    backgroundColor: series.label === "Other" ? OTHER_COLOR : RANK_COLORS[index % RANK_COLORS.length],
    stack: "drivers",
  }));

  return {
    type: "bar",
    data: {
      labels: data.buckets,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        // The legend shows ranks, not drivers; the tooltip reveals the actual
        // driver behind each segment (which differs bucket to bucket).
        tooltip: {
          callbacks: {
            // `item` is the general TooltipItem here (the config isn't narrowed to
            // "bar"), so read the type-agnostic formattedValue rather than parsed.y.
            label: (item) => {
              const name = data.point_names[item.dataIndex]?.[item.datasetIndex] ?? "Other";
              return `${name}: ${item.formattedValue}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0 },
          title: { display: true, text: "Rides" },
        },
      },
    },
  };
}
